import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import moment from "moment-timezone";
import { useTimezone } from "../../hooks/useTimezone";
import { toast } from "react-toastify";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { useStyles } from "./ScheduledIndividualMessageModalStyles";
import useScheduledIndividualMessage from "./useScheduledIndividualMessage";
import { green, red } from "@material-ui/core/colors";
import { getMinScheduledAt, STATUS_CFG, RECURRENCE_OPTIONS, SYSTEM_VARS } from "./scheduledMessage.constants";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Grid from "@material-ui/core/Grid";
import Menu from "@material-ui/core/Menu";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EventNoteIcon from "@material-ui/icons/EventNote";
import HistoryIcon from "@material-ui/icons/History";
import CloseIcon from "@material-ui/icons/Close";
import RepeatIcon from "@material-ui/icons/Repeat";
import SendIcon from "@material-ui/icons/Send";
import SettingsIcon from "@material-ui/icons/Settings";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────
const ScheduledIndividualMessageModal = ({ open, onClose, ticketId, editRecord = null }) => {
  // `nextExecutionAt` es HORA DE PARED de la empresa: no se convierte. Con `moment()` a
  // secas se leia en la zona del NAVEGADOR. Ver doc 0412 §11.
  const { formatHoraDePared } = useTimezone();
  const classes = useStyles();
  const {
    activeTab,
    attachment,
    body,
    bodyInputRef,
    cancelModalOpen,
    cancelTarget,
    companyTimezone,
    contactId,
    contactInputValue,
    contactName,
    contactNumber,
    contactOptions,
    fetchHistory,
    fileInputRef,
    handleCancelConfirm,
    handleClose,
    handleFileChange,
    handleInsertVar,
    handleRemoveFile,
    handleSubmit,
    handleTicketActionChange,
    isMounted,
    loadingContacts,
    loadingContext,
    loadingHistory,
    maxExecutions,
    queueIdAction,
    queues,
    recurrenceEndAt,
    recurrenceInterval,
    recurrenceType,
    resetForm,
    scheduledAt,
    selectedContact,
    selectedUserId,
    setActiveTab,
    setAttachment,
    setBody,
    setCancelModalOpen,
    setCancelTarget,
    setCompanyTimezone,
    setContactId,
    setContactInputValue,
    setContactName,
    setContactNumber,
    setContactOptions,
    setLoadingContacts,
    setLoadingContext,
    setLoadingHistory,
    setMaxExecutions,
    setQueueIdAction,
    setQueues,
    setRecurrenceEndAt,
    setRecurrenceInterval,
    setRecurrenceType,
    setScheduledAt,
    setSelectedContact,
    setSelectedUserId,
    setSimHistory,
    setSubmitting,
    setTicketAction,
    setTicketNumericId,
    setUsersList,
    setVarMenuAnchor,
    setWhatsappConnections,
    setWhatsappId,
    simHistory,
    submitting,
    ticketAction,
    ticketNumericId,
    user,
    usersList,
    varMenuAnchor,
    whatsappConnections,
    whatsappId,
  } = useScheduledIndividualMessage({ open, onClose, ticketId, editRecord });

  const renderStatusChip = status => {
    const cfg = STATUS_CFG[status] || { label: status, color: "#9ca3af" };
    return (
      <Chip
        label={cfg.label}
        className={classes.statusChip}
        style={{ backgroundColor: cfg.color, color: "#fff" }}
        size="small"
      />
    );
  };

  // ── Tab 0: Formulario ───────────────────────────────────────────────────────
  const renderForm = () => (
    <Box>

      {/* ── SELECTOR DE CONTACTO (solo cuando no viene de un ticket) ───────── */}
      {!ticketId && (
        <Box mb={1.5}>
          <Typography className={classes.sectionLabel}>Contacto</Typography>
          <Autocomplete
            options={contactOptions}
            loading={loadingContacts}
            value={selectedContact}
            onChange={(_, val) => {
              setSelectedContact(val);
              setContactId(val?.id   || null);
              setContactName(val?.name   || "");
              setContactNumber(val?.number || "");
            }}
            inputValue={contactInputValue}
            onInputChange={(_, val) => setContactInputValue(val)}
            getOptionLabel={opt => `${opt.name} — ${opt.number}`}
            getOptionSelected={(opt, val) => opt.id === val.id}
            noOptionsText="Sin resultados"
            renderInput={params => (
              <TextField
                {...params}
                label="Buscar contacto..."
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingContacts
                        ? <CircularProgress size={14} style={{ marginRight: 4 }} />
                        : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      )}

      {/* ── CONFIGURACIÓN DE ENVÍO ─────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Configuración de envío</Typography>
      <Grid container spacing={1} alignItems="flex-end">

        {/* Conexión WhatsApp */}
        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" margin="dense" size="small" fullWidth>
            <InputLabel>Conexión (WhatsApp)</InputLabel>
            <Select
              value={whatsappId}
              onChange={e => setWhatsappId(e.target.value)}
              label="Conexión (WhatsApp)"
            >
              {whatsappConnections.length === 0 && (
                <MenuItem value={whatsappId || ""} disabled>
                  {whatsappId ? `Canal #${whatsappId}` : "Sin conexiones"}
                </MenuItem>
              )}
              {whatsappConnections.map(w => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name || w.number || `Canal #${w.id}`}
                  {w.status !== "CONNECTED" && (
                    <Typography
                      component="span"
                      style={{ fontSize: "0.7rem", color: "#ef4444", marginLeft: 6 }}
                    >
                      desconectado
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Agente responsable — Select funcional */}
        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" margin="dense" size="small" fullWidth>
            <InputLabel>Agente responsable</InputLabel>
            <Select
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value)}
              label="Agente responsable"
            >
              {usersList.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                  {u.id === user.id && (
                    <Typography
                      component="span"
                      style={{ fontSize: "0.7rem", color: "#6b7280", marginLeft: 6 }}
                    >
                      (Tú)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider style={{ margin: "10px 0 4px" }} />

      {/* ── MENSAJE DE REMARKETING ─────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Mensaje de remarketing</Typography>

      {/* Textarea con botones flotantes en esquina inferior derecha */}
      <div className={classes.messageWrapper}>
        <TextField
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          size="small"
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="Ej: Hola {{name}}, te recordamos tu pago pendiente..."
          inputProps={{ maxLength: 4096 }}
          inputRef={bodyInputRef}
          helperText={`${body.length} / 4096`}
          style={{ paddingBottom: 0 }}
        />

        {/* Botones flotantes sobre el borde inferior del textarea */}
        <div className={classes.messageActions}>
          {/* Clip — adjuntar archivo */}
          <input
            type="file"
            ref={fileInputRef}
            className={classes.uploadInput}
            id="sim-file-upload"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={handleFileChange}
          />
          <label htmlFor="sim-file-upload">
            <Tooltip title="Adjuntar archivo">
              <IconButton
                component="span"
                size="small"
                className={classes.msgActionBtn}
              >
                <AttachFileIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </label>

          {/* Tuerca — variables del sistema */}
          <Tooltip title="Variables del sistema">
            <IconButton
              size="small"
              className={classes.msgActionBtn}
              onClick={e => setVarMenuAnchor(e.currentTarget)}
            >
              <SettingsIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Chip de archivo adjunto (debajo del textarea) */}
      {attachment && (
        <Box display="flex" alignItems="center" mt={0.5}>
          <Chip
            label={attachment.name}
            onDelete={handleRemoveFile}
            deleteIcon={<CloseIcon />}
            size="small"
            color="primary"
            variant="outlined"
            style={{ maxWidth: 260 }}
          />
        </Box>
      )}

      {/* Menú flotante de variables */}
      <Menu
        anchorEl={varMenuAnchor}
        open={Boolean(varMenuAnchor)}
        onClose={() => setVarMenuAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{ style: { minWidth: 170 } }}
      >
        {SYSTEM_VARS.map(v => (
          <MenuItem
            key={v.value}
            dense
            onClick={() => handleInsertVar(v.value)}
          >
            {v.label}
          </MenuItem>
        ))}
      </Menu>

      <Divider style={{ margin: "10px 0 4px" }} />

      {/* ── PROGRAMACIÓN HORARIA ──────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Programación horaria</Typography>
      <Grid container spacing={1} alignItems="flex-start">
        <Grid item xs={12} sm={6}>
          <TextField
            label="Fecha y hora de envío"
            type="datetime-local"
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            inputProps={{ min: getMinScheduledAt() }}
            InputLabelProps={{ shrink: true }}
          />
          {companyTimezone ? (
            <Typography
              variant="caption"
              style={{ color: "#10b981", display: "flex", alignItems: "center", gap: 3, marginTop: 2, paddingLeft: 2 }}
            >
              🌍 Hora en {companyTimezone}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" margin="dense" size="small" fullWidth>
            <InputLabel>Frecuencia</InputLabel>
            <Select
              value={recurrenceType}
              onChange={e => {
                const v = e.target.value;
                setRecurrenceType(v);
                if (v === "once") {
                  setRecurrenceInterval(1);
                  setRecurrenceEndAt("");
                  setMaxExecutions("");
                }
              }}
              label="Frecuencia"
            >
              {RECURRENCE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Campos extra de recurrencia */}
      {recurrenceType !== "once" && (
        <Grid container spacing={1} alignItems="flex-end" style={{ marginTop: 2 }}>
          {recurrenceType === "custom" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cada N días"
                type="number"
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                value={recurrenceInterval}
                onChange={e => setRecurrenceInterval(e.target.value)}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={recurrenceType === "custom" ? 6 : 6}>
            <TextField
              label="Máx. envíos (vacío = ilimitado)"
              type="number"
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              value={maxExecutions}
              onChange={e => setMaxExecutions(e.target.value)}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Fecha límite (opcional)"
              type="datetime-local"
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              value={recurrenceEndAt}
              onChange={e => setRecurrenceEndAt(e.target.value)}
              inputProps={{ min: scheduledAt || getMinScheduledAt() }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      )}

      <Divider style={{ margin: "10px 0 4px" }} />

      {/* ── ACCIONES CRM ────────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>
        Acciones de automatización CRM (Opcional)
      </Typography>
      <Grid container spacing={1} alignItems="flex-end">
        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" margin="dense" size="small" fullWidth>
            <InputLabel>Estado del ticket</InputLabel>
            <Select
              value={ticketAction}
              onChange={handleTicketActionChange}
              label="Estado del ticket"
            >
              <MenuItem value="none">Sin acción</MenuItem>
              <MenuItem value="open">Abrir / Reabrir ticket</MenuItem>
              <MenuItem value="close">Cerrar ticket</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            disabled={ticketAction === "none"}
          >
            <InputLabel>Transferir a departamento</InputLabel>
            <Select
              value={queueIdAction}
              onChange={e => setQueueIdAction(e.target.value)}
              label="Transferir a departamento"
            >
              <MenuItem value=""><em>Sin transferencia</em></MenuItem>
              {queues.map(q => (
                <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  // ── Tab 1: Historial ────────────────────────────────────────────────────────
  const renderHistory = () => {
    if (loadingHistory) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={26} />
        </Box>
      );
    }
    if (!simHistory.length) {
      return (
        <Typography className={classes.historyEmpty}>
          No hay mensajes programados para este contacto.
        </Typography>
      );
    }
    return (
      <List disablePadding>
        {simHistory.map(sim => {
          const canCancel  = ["pending", "paused"].includes(sim.status);
          const nextDate   = formatHoraDePared(sim.nextExecutionAt);
          const isRecurring = sim.recurrenceType !== "once";
          return (
            <ListItem key={sim.id} className={classes.historyItem} disableGutters>
              <ListItemText
                disableTypography
                primary={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography className={classes.historyBody}>{sim.body}</Typography>
                    {isRecurring && (
                      <Tooltip title={`Recurrencia: ${sim.recurrenceType}`}>
                        <RepeatIcon style={{ fontSize: 13, color: "#6b7280" }} />
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={
                  <Box display="flex" alignItems="center" style={{ gap: 6, marginTop: 3 }}>
                    {renderStatusChip(sim.status)}
                    <Typography className={classes.historyMeta}>
                      {nextDate}
                      {sim.executionCount > 0 && ` · ${sim.executionCount} enviado(s)`}
                    </Typography>
                  </Box>
                }
              />
              {canCancel && (
                <ListItemSecondaryAction>
                  <Tooltip title="Cancelar">
                    <IconButton
                      size="small"
                      className={classes.cancelBtn}
                      onClick={() => { setCancelTarget(sim); setCancelModalOpen(true); }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          );
        })}
      </List>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render principal
  // ─────────────────────────────────────────────────────────────────────────────
  // Formato idéntico al header del chat activo: "Alfredo H #722 - 51981155449"
  const bannerLabel = [
    contactName     || null,
    ticketNumericId ? `#${ticketNumericId}` : null,
    contactNumber   ? `- ${contactNumber}`  : null,
  ].filter(Boolean).join(" ");

  return (
    <>
      <ConfirmationModal
        title="Cancelar mensaje programado"
        open={cancelModalOpen}
        onClose={() => { setCancelModalOpen(false); setCancelTarget(null); }}
        onConfirm={handleCancelConfirm}
      >
        {cancelTarget && (
          <span>
            ¿Confirmas la cancelación del mensaje para{" "}
            <strong>{formatHoraDePared(cancelTarget.nextExecutionAt)}</strong>?
            Esta acción no se puede deshacer.
          </span>
        )}
      </ConfirmationModal>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">

        {/* ── Título + Banner + Tabs ────────────────────────────────────────── */}
        <DialogTitle disableTypography style={{ paddingBottom: 0 }}>

          {/* Fila de título */}
          <Box className={classes.titleRow}>
            <EventNoteIcon className={classes.titleIcon} />
            <Typography variant="h6" style={{ fontSize: "1rem", fontWeight: 700 }}>
              {editRecord ? "Editar Recordatorio" : "Programar Mensaje Individual"}
            </Typography>
            <Box flexGrow={1} />
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Banner de identidad: "Alfredo H #722 - 51981155449" */}
          {bannerLabel && (
            <Box className={classes.contactBanner} style={{ width: "100%" }}>
              <span className={classes.contactDot} />
              <Typography className={classes.contactText} component="span">
                {bannerLabel}
              </Typography>
            </Box>
          )}

          {/* Tabs — Historial solo visible cuando hay contexto de ticket */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            className={classes.mainTabs}
            indicatorColor="primary"
            textColor="primary"
            variant={ticketId ? "fullWidth" : "standard"}
          >
            <Tab
              label="Nuevo recordatorio"
              icon={<SendIcon style={{ fontSize: 15 }} />}
            />
            {ticketId && (
              <Tab
                label="Historial de envíos"
                icon={<HistoryIcon style={{ fontSize: 15 }} />}
              />
            )}
          </Tabs>
        </DialogTitle>

        {/* ── Contenido ─────────────────────────────────────────────────────── */}
        <DialogContent style={{ padding: "4px 16px 8px" }}>
          {loadingContext ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {activeTab === 0 && renderForm()}
              {activeTab === 1 && renderHistory()}
            </>
          )}
        </DialogContent>

        {/* ── Acciones ──────────────────────────────────────────────────────── */}
        {activeTab === 0 && (
          <DialogActions style={{ padding: "8px 16px" }}>
            <Button onClick={handleClose} size="small">
              Cancelar
            </Button>
            <div className={classes.btnWrapper}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={submitting || loadingContext || !contactId || !whatsappId}
                onClick={handleSubmit}
                startIcon={<EventNoteIcon />}
              >
                Programar
              </Button>
              {submitting && (
                <CircularProgress size={22} className={classes.buttonProgress} />
              )}
            </div>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ScheduledIndividualMessageModal;
